// src/components/HeroSection.jsx
import React from "react";
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";

/* -------------------- Helpers -------------------- */

const EASING = [0.22, 1, 0.36, 1];

function SmartImage({
  srcList = [],
  alt = "",
  className = "",
  loading = "lazy",
  fetchPriority,
  style,
  fallback,
}) {
  const [idx, setIdx] = React.useState(0);
  const exhausted = idx >= srcList.length;

  if (exhausted) {
    return (
      fallback ?? (
        <div
          className={`grid place-items-center ${className}`}
          style={{
            ...style,
            background:
              "linear-gradient(135deg, rgba(31,165,80,0.14), rgba(6,182,212,0.12))",
          }}
          aria-label={alt}
        >
          <span className="text-sm" style={{ color: "var(--muted, #6b7280)" }}>
            Preview coming soon…
          </span>
        </div>
      )
    );
  }

  return (
    <img
      src={srcList[idx] || ""}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      style={style}
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

function TiltCard({ children, className = "" }) {
  const prefersReduced = useReducedMotion();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tX = useTransform(ry, [-20, 20], [12, -12]);
  const tY = useTransform(rx, [-20, 20], [-12, 12]);

  function onMove(e) {
    if (prefersReduced) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    rx.set((0.5 - py) * 40);
    ry.set((px - 0.5) * 40);
  }

  function onLeave() {
    animate(rx, 0, { duration: 0.5, ease: EASING });
    animate(ry, 0, { duration: 0.5, ease: EASING });
  }

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, x: tX, y: tY, transformStyle: "preserve-3d" }}
      className={`[perspective:1000px] will-change-transform ${className}`}
      aria-hidden
    >
      {children}
    </motion.div>
  );
}

function Ticker({ words = [] }) {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) {
    return (
      <span className="font-semibold text-emerald-300">
        {words[0] ?? "Automation"}
      </span>
    );
  }
  return (
    <span className="relative inline-flex h-[1.6em] overflow-hidden align-[-0.2em] w-[10ch]">
      {words.map((w, i) => (
        <motion.span
          key={w}
          className="absolute left-0 top-0 w-full font-semibold text-emerald-300"
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{
            delay: i * 2,
            duration: 0.7,
            ease: EASING,
            repeat: Infinity,
            repeatDelay: (words.length - 1) * 2,
          }}
          style={{ willChange: "transform, opacity" }}
        >
          {w}
        </motion.span>
      ))}
    </span>
  );
}

function Particles({ count = 24 }) {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) return null;
  const dots = Array.from({ length: count });
  return (
    <div aria-hidden className="absolute inset-0 -z-10">
      {dots.map((_, i) => {
        const top = 5 + ((i * 211) % 90);
        const left = 5 + ((i * 97) % 90);
        const size = 3 + ((i * 13) % 6);
        const d = 6 + (i % 6);
        return (
          <motion.span
            key={i}
            className="absolute rounded-full bg-white/25"
            style={{ top: `${top}%`, left: `${left}%`, width: size, height: size }}
            animate={{ y: [0, -8, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: d, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
          />
        );
      })}
    </div>
  );
}

/* -------------------- Main -------------------- */

export default function HeroSection() {
  const prefersReduced = useReducedMotion();
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const root = document.documentElement;
    const byClass = root.classList.contains("dark");
    const byMedia =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    setIsDark(byClass || byMedia);
  }, []);

  const TOKENS = React.useMemo(
    () =>
      isDark
        ? {
            text: "#FFFFFF",
            muted: "rgba(255,255,255,0.80)",
            ring: "rgba(255,255,255,0.15)",
            panel: "rgba(16,21,34,0.72)",
            shadow: "0 24px 60px rgba(0,0,0,0.45)",
            bgOverlay1: "from-black/70 via-black/50 to-emerald-900/60",
            grid: "rgba(255,255,255,0.08)",
          }
        : {
            text: "#0B1220",
            muted: "rgba(17,24,39,0.75)",
            ring: "rgba(2,6,23,0.10)",
            panel: "rgba(255,255,255,0.75)",
            shadow: "0 26px 64px rgba(2,6,23,0.18)",
            bgOverlay1: "from-white/10 via-emerald-50/40 to-cyan-50/40",
            grid: "rgba(0,0,0,0.07)",
          },
    [isDark]
  );

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.08 } },
  };
  const fadeUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { delay, duration: 0.7, ease: EASING } },
  });
  const floatY = prefersReduced
    ? {}
    : { y: [0, -10, 0], transition: { duration: 4, repeat: Infinity, ease: "easeInOut" } };

  return (
    <motion.section
      role="banner"
      aria-label="MonkeyTech Hero Section"
      variants={container}
      initial="hidden"
      animate="show"
      className="relative overflow-hidden"
      style={{ color: TOKENS.text }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "url('/images/hero-bg.jpg'), url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(1.05)",
        }}
      />
      {/* Vignette + color wash */}
      <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${TOKENS.bgOverlay1} mix-blend-multiply`} />
      <div className="absolute inset-0 -z-10 [mask-image:radial-gradient(60%_60%_at_50%_30%,black,transparent)] bg-gradient-to-tr from-emerald-500/20 via-cyan-400/10 to-indigo-500/20" />
      {/* Soft grid */}
      <div
        className="absolute inset-0 pointer-events-none -z-10 opacity-[0.08]"
        style={{
          backgroundImage: `linear-gradient(${TOKENS.grid} 1px, transparent 1px), linear-gradient(90deg, ${TOKENS.grid} 1px, transparent 1px)`,
          backgroundSize: "42px 42px",
        }}
      />

      {/* Floating orbs */}
      {!prefersReduced && (
        <>
          <motion.div
            className="absolute -top-10 -left-10 h-56 w-56 rounded-full bg-emerald-400/30 blur-3xl"
            animate={{ y: [0, 20, 0], x: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-10 -right-10 h-64 w-64 rounded-full bg-cyan-400/25 blur-3xl"
            animate={{ y: [0, -20, 0], x: [0, -10, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      {/* Content */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <motion.div variants={container} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <motion.img
              variants={fadeUp(0)}
              src="/images/logo.png"
              alt="MonkeyTech logo"
              className="mx-auto lg:mx-0 h-16 sm:h-20 mb-6 drop-shadow-xl"
              loading="eager"
              onError={(e) => {
                e.currentTarget.src =
                  "https://via.placeholder.com/160x64.png?text=MonkeyTech";
              }}
            />

            <motion.h1
              variants={fadeUp(0.05)}
              className="font-inter tracking-tight text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-tight"
            >
              Build once. <span className="block text-emerald-400">Scale everywhere.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp(0.12)}
              className="mt-4 sm:mt-6 text-base sm:text-xl max-w-2xl mx-auto lg:mx-0"
              style={{ color: TOKENS.muted }}
            >
              We turn offline businesses into powerful digital brands with automation, AI tooling,
              and premium web/app experiences tailored for growth.
            </motion.p>

            <motion.div
              variants={fadeUp(0.18)}
              className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 ring-1 backdrop-blur"
              style={{ background: "rgba(255,255,255,0.08)", borderColor: TOKENS.ring }}
            >
              <span className="text-sm" style={{ color: TOKENS.muted }}>
                Focus areas:
              </span>
              <Ticker words={["AI Automations", "E-commerce", "Branding", "Web & App"]} />
            </motion.div>

            <motion.div
              variants={fadeUp(0.24)}
              className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <motion.a
                href="/services"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center px-7 py-3 rounded-xl bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-800/30 hover:bg-emerald-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400/60"
                aria-label="Explore MonkeyTech services"
              >
                Explore Now
              </motion.a>

              <motion.a
                href="/contact"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center px-7 py-3 rounded-xl bg-white/10 text-white font-semibold ring-1 ring-white/20 hover:bg-white/15 backdrop-blur focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
                aria-label="Contact MonkeyTech"
              >
                Let’s Talk
              </motion.a>
            </motion.div>

            <motion.ul
              variants={fadeUp(0.3)}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto lg:mx-0"
              aria-label="MonkeyTech performance stats"
            >
              {[
                { k: "+10", v: "Projects" },
                { k: "99.9%", v: "Uptime" },
                { k: "4.9/5", v: "CSAT" },
                { k: "+300", v: "Automations" },
              ].map((s) => (
                <li
                  key={s.v}
                  className="rounded-2xl p-4 backdrop-blur ring-1"
                  style={{
                    background: "rgba(255,255,255,0.10)",
                    borderColor: TOKENS.ring,
                  }}
                >
                  <div className="text-2xl font-extrabold">{s.k}</div>
                  <div className="text-sm" style={{ color: TOKENS.muted }}>
                    {s.v}
                  </div>
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Right: 3D mock + image */}
          <div className="lg:col-span-5">
            <TiltCard>
              <motion.div
                style={{ transformStyle: "preserve-3d" }}
                className="relative mx-auto max-w-md w-full"
                variants={fadeUp(0.1)}
                animate={floatY}
              >
                <div
                  className="relative rounded-3xl p-6 sm:p-8 backdrop-blur-xl ring-1 shadow-2xl"
                  style={{ transform: "translateZ(30px)", background: TOKENS.panel, borderColor: TOKENS.ring }}
                >
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-xl bg-emerald-500/90 grid place-items-center font-bold text-white">
                      MT
                    </span>
                    <div>
                      <p className="text-sm" style={{ color: TOKENS.muted }}>
                        Showcase
                      </p>
                      <p className="font-semibold">Web & Automation</p>
                    </div>
                  </div>

                  {/* IMAGE with smart fallback */}
                  <div className="mt-5 overflow-hidden rounded-2xl ring-1" style={{ borderColor: TOKENS.ring }}>
                    <SmartImage
                      alt="MonkeyTech sample UI"
                      className="block w-full h-56 object-cover"
                      srcList={[
                        "/images/hero-mock.jpg", // <- add this to public/images
                        "/images/hero-mock.png",
                        "https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&w=1280&q=80",
                        "https://via.placeholder.com/1280x720.png?text=MonkeyTech+UI",
                      ]}
                      fallback={
                        <div className="h-56 grid place-items-center bg-gradient-to-br from-emerald-500/15 to-cyan-500/15">
                          <span className="text-sm" style={{ color: TOKENS.muted }}>
                            Preview coming soon…
                          </span>
                        </div>
                      }
                    />
                  </div>

                  {/* Feature pills */}
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {[
                      { t: "Catalog", d: "Sync SKUs" },
                      { t: "Payments", d: "Smart retries" },
                      { t: "Marketing", d: "1-click reels" },
                      { t: "Analytics", d: "Realtime" },
                    ].map((f) => (
                      <div
                        key={f.t}
                        className="rounded-2xl p-3 ring-1"
                        style={{ background: "rgba(255,255,255,0.10)", borderColor: TOKENS.ring }}
                      >
                        <p className="text-sm" style={{ color: TOKENS.muted }}>
                          {f.t}
                        </p>
                        <p className="text-xs" style={{ color: TOKENS.muted }}>
                          {f.d}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div
                    className="mt-6 rounded-2xl p-4 ring-1"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(16,185,129,0.18), rgba(6,182,212,0.18))",
                      borderColor: TOKENS.ring,
                    }}
                  >
                    <p className="text-sm">
                      “Set it once and watch orders flow across channels automatically.”
                    </p>
                  </div>
                </div>

                <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-r from-emerald-400/20 via-teal-400/10 to-cyan-400/20 blur-2xl" />
              </motion.div>
            </TiltCard>
          </div>
        </motion.div>

        {/* Trust bar */}
        <motion.div
          variants={fadeUp(0.1)}
          className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 opacity-90"
          aria-label="Trusted by brands"
        >
          {["Kalamatwala", "Madhusudan Tex Fab", "D2C", "Retail+"].map((n) => (
            <span key={n} className="text-sm sm:text-base" style={{ color: TOKENS.muted }}>
              {n}
            </span>
          ))}
        </motion.div>

        {/* Scroll cue */}
        <motion.a
          href="#features"
          variants={fadeUp(0.15)}
          className="group mt-10 mx-auto w-max inline-flex items-center gap-2"
          style={{ color: TOKENS.muted }}
          aria-label="Scroll to features"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="opacity-80 group-hover:opacity-100">
            <path d="M12 5v14m0 0l-6-6m6 6l6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-sm">See how it works</span>
        </motion.a>
      </div>

      {/* Particles */}
      {!prefersReduced && <Particles count={24} />}

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />

      {/* Fonts helper */}
      <style>{`
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
      `}</style>

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "MonkeyTech",
            url: "https://www.monkeytech.com",
            description:
              "Revolutionizing digital solutions with cutting-edge technology and innovative approaches.",
            publisher: {
              "@type": "Organization",
              name: "MonkeyTech",
              logo: { "@type": "ImageObject", url: "/images/logo.png" },
            },
          }),
        }}
      />
    </motion.section>
  );
}
